import AlertComponent from "@/components/sharedComponents/alert/alert.component";
import { EventBus } from "@/event-bus";
import { AccountManagement } from "@/interfaces/accountManagement";
import { ModalNotification } from "@/interfaces/notification";
import { UserBiotrackConfig, UserMetrcConfig } from "@/interfaces/traceability";
import {
  pharmacistDefault,
  PharmacistLocationInterface,
  User,
  userDefault
} from "@/interfaces/user";
import { policyList } from "@/internal";
import {
  biotrackFormDetail,
  metrcFormDetail,
  pharmacistForm,
  userFormDetail,
  userFormMetadata
} from "@/metadata/user";
import { i18n } from "@/plugins/i18n";
import { messagesService } from "@/services/messages.service";
import { traceabilityService } from "@/services/traceability.service";
import { store } from "@/store";
import { Callback } from "@/types/types";
import {
  BooleanCheck,
  DynamicFormComponent,
  FormField,
  FormModel,
  FormResponse,
  PageTitleComponent
} from "helix-vue-components";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter, Mutation } from "vuex-class";
import Template from "./UserManagementForm.template.vue";

const namespace: string = "UserModule";

@Component({
  mixins: [Template],
  components: {
    DynamicFormComponent,
    PageTitleComponent
  }
})
export default class UserManagementFormComponent extends Vue {
  public static currentBiotrackStatus: boolean = false;
  @Getter("currentUser", { namespace })
  public current!: FormModel | null;
  @Getter("pharmacistInfo", { namespace })
  public pharmacistInfo!: FormModel | null;
  @Getter("loading", { namespace })
  public loading!: boolean;
  @Action("save", { namespace })
  public saveUser!: Callback;
  @Action("findUser", { namespace })
  public findUser!: Callback;
  @Action("getPharmacistInfo", { namespace })
  public getPharmacistInfo!: Callback;
  @Getter("availability", { namespace: "AccountManagementModule" })
  public availability!: AccountManagement.Availability;
  @Mutation("setDisabledMode", { namespace })
  public disabledMode!: Callback;
  @Getter("hasPermission", { namespace: "authModule" })
  public hasPermission!: BooleanCheck;

  public fieldsConfig: FormField[] = cloneDeep(userFormMetadata);
  public fieldsConfigDetail: FormField[] = cloneDeep(userFormDetail);
  public form!: DynamicFormComponent;
  public formDetail!: DynamicFormComponent;
  public model = cloneDeep(userDefault);
  public pharmacistDataModel = cloneDeep(pharmacistDefault);
  public metrcConfig: UserMetrcConfig | null = null;
  public biotrackConfig: UserBiotrackConfig | null = null;
  public currentTraceability = {
    biotrackConfiguration: 0,
    metrcConfiguration: 0
  };
  public tabAdded: Boolean = false;
  public metrcTraceabilityTabDetails: FormField[] = cloneDeep(metrcFormDetail);
  public biotrackTraceabilityTabDetails: FormField[] = cloneDeep(
    biotrackFormDetail
  );
  public pharmacistTabDetails: FormField[] = cloneDeep(pharmacistForm);
  public invalidDocument = false;
  public fileExtension = "";
  public fileSize = 0;
  public invalidFileName: boolean = false;
  public formChange(form: any) {
    if (!form.model.avatar) {
      this.invalidDocument = false;
      return;
    }
    // getting file extension
    this.fileExtension = form.model.avatar.name.match(/\.([^\.]+)$/)![1];
    // getting file size in "mega bytes" by dividing with 1000000
    this.fileSize = form.model.avatar.size / 1000000;
    // only " alphanumeric,_ , - , ." characters are allowed in file name
    if (/([^a-zA-Z0-9_.\-])/.test(form.model.avatar.name)) {
      this.invalidFileName = true;
    } else {
      this.invalidFileName = false;
    }
    if (
      !store.getters["authModule/fileImageExtention"].includes(
        this.fileExtension
      ) ||
      this.fileSize > store.getters["authModule/fileUploadLimit"] ||
      this.invalidFileName
    ) {
      this.invalidDocument = true;
    } else {
      this.invalidDocument = false;
    }
  }
  public async save() {
    if (this.invalidDocument) {
      const invalidDocMsg: string | string[] = [];
      if (
        !store.getters["authModule/fileImageExtention"].includes(
          this.fileExtension
        )
      ) {
        invalidDocMsg.push(
          String(
            this.$t("security.invalid_image_files_extension", {
              fileExtention: store.getters["authModule/fileImageExtention"]
            })
          )
        );
      }
      if (this.fileSize > store.getters["authModule/fileUploadLimit"]) {
        invalidDocMsg.push(
          String(
            this.$t("security.invalid_file_size", {
              fileSize: store.getters["authModule/fileUploadLimit"]
            })
          )
        );
      }
      if (this.invalidFileName) {
        invalidDocMsg.push(String(this.$t("security.invalid_file_name")));
      }
      messagesService.showMessage(
        "fal fa-exclamation-triangle",
        invalidDocMsg,
        "error"
      );
      return;
    }
    const resp: FormResponse = await this.form.submit();
    const respDetail: FormResponse = await this.formDetail.submit();

    if (resp.valid && respDetail.valid) {
      const newModel: any = {
        ...resp.currentModel,
        ...respDetail.currentModel
      };
      newModel.redirectTo = "user-management-list";
      if (isEqual(this.metrcConfig, newModel.metrc)) {
        newModel.metrc = null;
      }
      if (isEqual(this.biotrackConfig, newModel.biotrack)) {
        newModel.metrc = null;
      }
      if (newModel.pharmacistData) {
        newModel.pharmacistData.user_id = this.$route.params.id
          ? parseInt(this.$route.params.id, undefined)
          : 0;

        newModel.pharmacistData.pharmacist_enable = parseInt(
          newModel.pharmacist,
          undefined
        );
      }
      this.saveUser(newModel);
    }
  }

  public cancel() {
    this.$router.push({ name: "user-management-list" });
  }

  public checkSeats() {
    if (this.availability && !this.availability.users.available) {
      EventBus.$emit("modal", {
        title: "no_seats_available",
        body: "msg_no_seats",
        captionButton: "OK"
      });
      this.$router.push({ name: "user-management-list" });
    }
  }

  public addPharmacistInfoTab(isSetPharmacistData: boolean, value?: any) {
    const modelInit = cloneDeep(this.model);
    const tabIndex =
      this.currentTraceability.biotrackConfiguration === 0
        ? "/2"
        : this.currentTraceability.metrcConfiguration === 0
        ? "/3"
        : "/4";
    const pharmacistTabArray = this.fieldsConfigDetail.filter(
      el => el.tab === i18n.t("pharmacist_info") + tabIndex
    );
    const pharmacistTabConfig = cloneDeep(this.pharmacistTabDetails);
    if (pharmacistTabArray.length === 0) {
      pharmacistTabConfig.map(formData => {
        formData.tab = formData.tab + tabIndex;
      });
      this.fieldsConfigDetail = cloneDeep([
        ...this.fieldsConfigDetail,
        ...pharmacistTabConfig
      ]);
    }
    modelInit.pharmacist = "1";
    setTimeout(() => {
      if (isSetPharmacistData) {
        this.setPharmacistData();
      }
      this.model = value ? value.model : cloneDeep(modelInit);
    }, 50);
  }

  public setPharmacistData() {
    const pharmacistLocationInfo: number[] = [];
    if (this.pharmacistInfo) {
      if (
        this.pharmacistInfo!.user_location &&
        this.pharmacistInfo!.user_location.length !== 0
      ) {
        this.pharmacistInfo!.user_location.forEach(
          (pharmacistLocation: PharmacistLocationInterface) =>
            pharmacistLocationInfo.push(pharmacistLocation.location_id)
        );
      }
      this.pharmacistDataModel.pharmacist_title = this.pharmacistInfo!.pharmacist_title;
      this.pharmacistDataModel.pharmacist_license_number = this.pharmacistInfo!.pharmacist_license_number;
      this.pharmacistDataModel.pharmacist_national_provider_identifier = this.pharmacistInfo!.pharmacist_national_provider_identifier;
      this.pharmacistDataModel.pharmacist_enable = 1;
      this.pharmacistDataModel.pharmacist_location.add = pharmacistLocationInfo;
      this.pharmacistDataModel.pharmacist_location.delete = pharmacistLocationInfo;
    }
    this.model.pharmacistData = this.pharmacistDataModel;
    this.tabAdded = true;
  }

  public async renderFormTab() {
    const modelInit = cloneDeep(this.model);
    this.currentTraceability = await traceabilityService.getUserCurrentTraceability(
      this.current!.id!
    );

    const biotrackTraceabilityTabConfig = cloneDeep(
      this.biotrackTraceabilityTabDetails
    );
    const metrcTraceabilityTabConfig = cloneDeep(
      this.metrcTraceabilityTabDetails
    );
    if (this.currentTraceability.biotrackConfiguration) {
      if (!UserManagementFormComponent.currentBiotrackStatus) {
        const userNameIndex = biotrackTraceabilityTabConfig.findIndex(
          index => index.id === "user_name"
        );
        const inputPassIndex = biotrackTraceabilityTabConfig.findIndex(
          index => index.id === "input_pass"
        );
        biotrackTraceabilityTabConfig![
          userNameIndex
        ]!.options!.validationRules = ["required"];
        biotrackTraceabilityTabConfig![
          inputPassIndex
        ]!.options!.validationRules = ["required"];
      }
      biotrackTraceabilityTabConfig.map(formData => {
        formData.tab = formData.tab + "/2";
      });
      this.fieldsConfigDetail = cloneDeep([
        ...this.fieldsConfigDetail,
        ...biotrackTraceabilityTabConfig
      ]);
    }
    if (this.currentTraceability.metrcConfiguration) {
      metrcTraceabilityTabConfig.map(formData => {
        formData.tab = formData.tab + "/2";
      });
      this.fieldsConfigDetail = cloneDeep([
        ...this.fieldsConfigDetail,
        ...metrcTraceabilityTabConfig
      ]);
    }
    this.fieldsConfigDetail = cloneDeep([...this.fieldsConfigDetail]);
    if (this.pharmacistInfo && this.pharmacistInfo.pharmacist_enable === 1) {
      modelInit.pharmacist = "1";
      this.addPharmacistInfoTab(true);
    } else {
      modelInit.pharmacist = "0";
      this.tabAdded = false;
    }
    modelInit.pharmacistData = this.pharmacistDataModel;
    setTimeout(() => {
      this.model = cloneDeep(modelInit);
    }, 50);
  }

  public created() {
    if (this.$route.name === "user-management-view") {
      this.disabledMode(true);
    }
    this.fieldsConfigDetail.map(field => {
      if (field.id === "datepicker") {
        if (this.$route.name === "user-management-view") {
          field.options!.pickerOptions!.disabled = true;
        } else {
          field.options!.pickerOptions!.disabled = false;
        }
      }
    });
  }
  public async mounted() {
    this.getTraceStatus();
    this.form = this.$refs["user-form"] as DynamicFormComponent;
    this.formDetail = this.$refs["user-detail-form"] as DynamicFormComponent;
    this.$store.commit("UserModule/setPharmacistInfoFetched", false);
    if (this.$route.params.id) {
      await Promise.all([
        this.findUser(this.$route.params.id),
        this.getPharmacistInfo(this.$route.params.id)
      ]);

      this.model =
        ((this.current && cloneDeep(this.current)) as User) ||
        cloneDeep(this.model);
      this.biotrackConfig = await traceabilityService.getUserBiotrackConfig(
        this.current ? this.current!.id! : 0
      );
      this.model.biotrack = this.biotrackConfig;
      if (!!+process.env.VUE_APP_METRC) {
        this.metrcConfig = await traceabilityService.getUserMetrcConfig(
          this.current ? this.current!.id! : 0
        );
      }
    } else {
      this.$watch("availability", this.checkSeats);
    }
    this.$store.commit("UserModule/setPharmacistInfoFetched", true);
    const modelInit = this.current || this.model;

    modelInit.hasMetrcConfig = !!this.metrcConfig;
    modelInit.hasBiotrackConfig = !!this.biotrackConfig;
    if (this.biotrackConfig) {
      modelInit.biotrack = cloneDeep(this.biotrackConfig);
    }

    if (this.$route.name === "user-management-add") {
      this.fieldsConfigDetail = cloneDeep(userFormDetail);
      this.model = cloneDeep(userDefault);
    } else {
      this.model = modelInit as User;
    }
    this.pharmacistDataModel.user_id = this.$route.params.id
      ? parseInt(this.$route.params.id, undefined)
      : 0;

    this.renderFormTab();
    EventBus.$on("metrUserTest", this.showMetrcTestMessage);
    EventBus.$on("biotrackUserTest", this.showBiotrackTestMessage);
  }

  public async modelChanged(value: any) {
    if (value.modelField === "pharmacist") {
      const model = value.model[value.modelField];
      if (model === "1") {
        if (this.$route.params.id) {
          await this.getPharmacistInfo(this.$route.params.id);
          this.addPharmacistInfoTab(true, value);
        } else {
          this.addPharmacistInfoTab(false, value);
        }
      }
      if (model === "0") {
        const modelInit = value.model;
        const tabIndex =
          this.currentTraceability.biotrackConfiguration === 0
            ? "/2"
            : this.currentTraceability.metrcConfiguration === 0
            ? "/3"
            : "/4";
        modelInit.pharmacist = "0";
        const configFields = this.fieldsConfigDetail.filter(
          el => el.tab !== i18n.t("pharmacist_info") + tabIndex
        );
        this.fieldsConfigDetail = cloneDeep(configFields);
        this.tabAdded = false;
        setTimeout(() => {
          this.model = cloneDeep(modelInit);
        }, 50);
      }
    }
  }

  public showMetrcTestMessage(res: boolean) {
    const msgModal: ModalNotification = {
      title: "traceability.integration_form.metrc_enabled",
      body: "traceability.integration_form.mtrc_msg_ok",
      captionButton: "OK",
      titleClass: "success_title"
    };

    if (!res) {
      msgModal.title = "smth_wrong";
      msgModal.body = "traceability.integration_form.metrc_msg_fail";
      msgModal.titleClass = "error_title";
    }
    this.$modals.load(
      AlertComponent,
      { size: "normal", positionX: "center", positionY: "center" },
      { msgModal }
    );
  }
  public showBiotrackTestMessage(res: boolean) {
    const msgModal: ModalNotification = {
      title: "traceability.integration_form.biotrack_enabled",
      body: "traceability.integration_form.biotrack_msg_ok",
      captionButton: "OK",
      titleClass: "success_title"
    };

    if (!res) {
      msgModal.title = "smth_wrong";
      msgModal.body = "traceability.integration_form.biotrack_msg_fail";
      msgModal.titleClass = "error_title";
    }
    this.$modals.load(
      AlertComponent,
      { size: "normal", positionX: "center", positionY: "center" },
      { msgModal }
    );
  }

  public async getTraceStatus() {
    const tracestatus = await traceabilityService.validateUserCredentials();
    UserManagementFormComponent.currentBiotrackStatus =
      tracestatus.status === "error" && tracestatus.code === 200 ? true : false;
  }

  protected getTraceability(locationId: number | null): Promise<string> {
    return traceabilityService
      .getCurrentTraceability({ location_id: locationId })
      .then(value => {
        const config = value.find(v => {
          return (
            v.location_id === this.model.settings!.current_location_id &&
            v.schema.name === "traceability"
          );
        });
        return (config && config.value) || "None";
      });
  }

  protected beforeDestroy() {
    this.$store.commit("UserModule/setPharmacistInfo", null);
  }
}
